import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Button, Table, Thead, Tbody, Tr, Th, Td, FormControl, FormLabel, Input, useToast, HStack, Spinner, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Flex, Text, IconButton
} from '@chakra-ui/react';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';

const BarbeirosPage = () => {
  const [barbeiros, setBarbeiros] = useState([]);
  const [form, setForm] = useState({ nome: '', telefone: '' });

  // Função utilitária para formatar telefone BR
  function formatTelefoneBR(telefone) {
    if (!telefone) return '';
    const num = telefone.replace(/\D/g, '');
    if (num.length === 11) {
      return `(${num.slice(0, 2)}) ${num.slice(2, 7)}-${num.slice(7, 11)}`;
    } else if (num.length === 10) {
      return `(${num.slice(0, 2)}) ${num.slice(2, 6)}-${num.slice(6, 10)}`;
    }
    return telefone;
  }

  // Máscara para input de telefone
  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 10) {
      value = value.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    } else if (value.length > 6) {
      value = value.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else if (value.length > 2) {
      value = value.replace(/(\d{2})(\d{0,5})/, '($1) $2');
    }
    setForm({ ...form, telefone: value });
  };

  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();

  const fetchBarbeiros = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('barbeiros').select('*').order('criado_em', { ascending: false });
    if (!error) setBarbeiros(data);
    setLoading(false);
  };

  useEffect(() => { fetchBarbeiros(); }, []);

  const openModal = (barbeiro = null) => {
    if (barbeiro) {
      setForm({ nome: barbeiro.nome, telefone: barbeiro.telefone || '' });
      setEditingId(barbeiro.id);
    } else {
      setForm({ nome: '', telefone: '' });
      setEditingId(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ nome: '', telefone: '' });
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.nome) {
      toast({ title: 'Nome é obrigatório', status: 'warning' });
      return;
    }
    setLoading(true);
    let result;
    if (editingId) {
      result = await supabase.from('barbeiros').update(form).eq('id', editingId);
    } else {
      result = await supabase.from('barbeiros').insert([form]);
    }
    setLoading(false);
    if (!result.error) {
      toast({ title: editingId ? 'Barbeiro atualizado!' : 'Barbeiro cadastrado!', status: 'success' });
      fetchBarbeiros();
      closeModal();
    } else {
      toast({ title: 'Erro ao salvar barbeiro', description: result.error.message, status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este barbeiro?')) return;
    const { error } = await supabase.from('barbeiros').delete().eq('id', id);
    if (!error) {
      toast({ title: 'Barbeiro excluído', status: 'info' });
      fetchBarbeiros();
    } else {
      toast({ title: 'Erro ao excluir', description: error.message, status: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box mt={{ base: 6, md: 0 }}>
        <HStack justify="space-between" mb={8}>
          <Heading size="lg">Barbeiros</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal()}>Adicionar Barbeiro</Button>
        </HStack>
        {loading ? <Spinner /> : (
           <Box>
            {/* Tabela Desktop */}
            <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
              <Table variant="striped" colorScheme="blackAlpha">
                <Thead>
                  <Tr>
                    <Th>Nome</Th>
                    <Th>Telefone</Th>
                    <Th>Data de Cadastro</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {barbeiros.map(b => (
                    <Tr key={b.id}>
                      <Td>{b.nome}</Td>
                      <Td>{b.telefone ? formatTelefoneBR(b.telefone) : '-'}</Td>
                      <Td>{b.criado_em ? new Date(b.criado_em).toLocaleString('pt-BR') : '-'}</Td>
                      <Td>
                        <HStack>
                          <Button size="sm" colorScheme="yellow" variant="outline" onClick={() => openModal(b)}>Editar</Button>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => handleDelete(b.id)}>Excluir</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            {/* Cards Mobile */}
            <Box display={{ base: 'block', md: 'none' }}>
              {barbeiros.map(b => (
                <Box key={b.id} borderWidth={1} borderRadius="lg" p={4} mb={4} bg="gray.900" color="white" boxShadow="md">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold" fontSize="lg">{b.nome}</Text>
                    <HStack>
                      <IconButton icon={<EditIcon />} aria-label="Editar" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal(b)} />
                      <IconButton icon={<DeleteIcon />} aria-label="Excluir" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => handleDelete(b.id)} />
                    </HStack>
                  </Flex>
                  <Text fontSize="sm"><b>Telefone:</b> {b.telefone ? formatTelefoneBR(b.telefone) : '-'}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {/* Modal de cadastro/edição */}
        <Modal isOpen={modalOpen} onClose={closeModal} size={{ base: "md", md: "lg" }} isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
          <ModalContent 
            bg="gray.800" 
            color="white" 
            borderRadius="xl" 
            boxShadow="xl"
            mx={{ base: 3, md: 0 }}
            my={{ base: 3, md: 0 }}
            maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
            overflowY="auto"
          >
            <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex align="center">
                {editingId ? (
                  <EditIcon color="yellow.300" mr={3} />
                ) : (
                  <AddIcon color="yellow.300" mr={3} />
                )}
                <Text fontSize="xl" fontWeight="bold" color="white">
                  {editingId ? 'Editar Barbeiro' : 'Novo Barbeiro'}
                </Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={6} px={{ base: 4, md: 6 }}>
              <FormControl isRequired mb={5}>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome do Barbeiro</FormLabel>
                <Input 
                  value={form.nome} 
                  onChange={e => setForm({ ...form, nome: e.target.value })} 
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="Nome completo"
                />
              </FormControl>
              
              <FormControl mb={5}>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Telefone</FormLabel>
                <Input 
                  value={form.telefone} 
                  onChange={handleTelefoneChange} 
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="(00) 00000-0000"
                  maxLength={16}
                />
              </FormControl>
            </ModalBody>
            
            <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
                <Button 
                  variant="outline" 
                  onClick={closeModal} 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ bg: "gray.700" }}
                  width={{ base: "100%", sm: "auto" }}
                >
                  Cancelar
                </Button>
                <Button 
                  colorScheme="yellow" 
                  onClick={handleSave} 
                  isLoading={loading}
                  leftIcon={editingId ? <EditIcon /> : <AddIcon />}
                  width={{ base: "100%", sm: "auto" }}
                  _hover={{ bg: "yellow.500" }}
                >
                  {editingId ? 'Atualizar Barbeiro' : 'Salvar Barbeiro'}
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </AdminLayout>
  );
};

export default BarbeirosPage;
